import React, { useState, useEffect, useCallback } from 'react';
import { TenantService } from '../platform/services/TenantService';
import { FirebaseService } from '../shared/services/FirebaseService';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import ErrorBoundary from '../shared/components/ErrorBoundary';
import TenantUserManagement from './components/TenantUserManagement';
import TenantNavBar from './components/TenantNavBar';
import { getTenantConfig } from './registry';

// Routing Product
import RoutingDashboard from '../apps/routing/components/RoutingDashboard';

// Customer Apps (will be imported dynamically)
// import ChainToApp from './chainto/ChainToApp';

import {
  Building2,
  AlertCircle,
  Settings,
  BarChart3,
  FileText,
  Package,
  ArrowLeft,
  Database,
  Upload,
  Users,
  Truck
} from 'lucide-react';

// Safe number and calculation helpers
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'number' && isNaN(value)) return defaultValue;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const safeDivision = (numerator, denominator, defaultValue = 0) => {
  const num = safeNumber(numerator);
  const den = safeNumber(denominator);
  if (den === 0) return defaultValue;
  const result = num / den;
  return isNaN(result) ? defaultValue : result;
};

const safeDateDiff = (startDate, endDate, defaultValue = 0) => {
  try {
    if (!startDate || !endDate) return defaultValue;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return defaultValue;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return isNaN(diffDays) ? defaultValue : diffDays;
  } catch {
    return defaultValue;
  }
};

/**
 * TenantRouter - Routes to specific tenant applications
 * Determines which customer app to load based on tenant configuration
 */
export default function TenantRouter({ companyId, userProfile, onReturnToPlatform }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [activeApp, setActiveApp] = useState('dashboard');

  /**
   * Load tenant data and determine which app to show
   */
  const loadTenantData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🏢 Loading tenant data for: ${companyId}`);
      console.log('🔍 DEBUG: companyId =', companyId, 'checking for inmocean');
      const tenantData = await TenantService.getTenant(companyId);
      setTenant(tenantData);

      // Determine primary app based on tenant type. Registered tenants own
      // their bespoke dashboard tab through src/tenants/registry.js; unknown
      // tenants fall through to the older routing/default logic.
      const simpleId = companyId.replace('company_', '');
      if (getTenantConfig(companyId)) {
        setActiveApp('dashboard');
      } else if (simpleId === 'inmocean') {
        setActiveApp('dashboard');
      } else if (tenantData.features?.chainto) {
        setActiveApp('chainto');
      } else {
        // Default to routing for basic routing product tenants
        setActiveApp('routing');
      }

      console.log(`✅ Tenant loaded: ${tenantData.name}`);

    } catch (err) {
      console.error('❌ Failed to load tenant:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadTenantData();
    }
  }, [companyId, loadTenantData]);

  /**
   * Handle app switching within tenant
   */
  const handleAppSwitch = (appName) => {
    console.log(`🔄 Switching to app: ${appName}`);
    setActiveApp(appName);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <h3 className="text-lg font-semibold text-gray-900 mt-4">Loading Tenant</h3>
          <p className="text-gray-600 mt-2">Initializing {companyId}...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Tenant Load Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={loadTenantData}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={onReturnToPlatform}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Return to Platform
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No tenant found
  if (!tenant) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Building2 className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tenant Not Found</h3>
          <p className="text-gray-600 mb-4">The requested tenant could not be found.</p>
          <button
            onClick={onReturnToPlatform}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Platform
          </button>
        </div>
      </div>
    );
  }

  // Look up the tenant's config from the registry. If found, render the
  // registered-tenant chrome (parameterized TenantNavBar). If not, fall
  // through to the existing general-tenant interface (unchanged).
  const tenantConfig = getTenantConfig(companyId);

  // Registered tenant — render the bespoke chrome + AppComponent
  if (tenantConfig) {
    return (
      <TenantNavBar
        tenantConfig={tenantConfig}
        companyId={companyId}
        tenant={tenant}
        userProfile={userProfile}
        activeApp={activeApp}
        onTabChange={setActiveApp}
        onReturnToPlatform={onReturnToPlatform}
      />
    );
  }

// GENERAL TENANT INTERFACE (for all other tenants)
return (
  <ErrorBoundary fallbackMessage="Something went wrong with the tenant application">
    <div className="min-h-screen bg-gray-50">
      {/* Tenant Navigation - Platform Owners Only */}
      {userProfile?.role === 'platform_owner' && (
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
            <div className="flex justify-between items-center py-3">
              {/* Left side - Tenant info and navigation */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={onReturnToPlatform}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Return to Platform"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h2 className="font-semibold text-gray-900">{tenant.name}</h2>
                    <p className="text-sm text-gray-600">{tenant.industry || 'Business Intelligence'}</p>
                  </div>
                </div>

                {/* App Navigation */}
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => handleAppSwitch('dashboard')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeApp === 'dashboard'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <BarChart3 size={16} className="inline mr-2" />
                    Dashboard
                  </button>

                  <button
                    onClick={() => handleAppSwitch('routing')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeApp === 'routing'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Truck size={16} className="inline mr-2" />
                    Routing
                  </button>

                  {tenant.features?.chainto && (
                    <button
                      onClick={() => handleAppSwitch('chainto')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeApp === 'chainto'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Package size={16} className="inline mr-2" />
                      ChainTo
                    </button>
                  )}

                  <button
                    onClick={() => handleAppSwitch('documents')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeApp === 'documents'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <FileText size={16} className="inline mr-2" />
                    Documents
                  </button>

                  <button
                    onClick={() => handleAppSwitch('team')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeApp === 'team'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Users size={16} className="inline mr-2" />
                    Team
                  </button>

                  <button
                    onClick={() => handleAppSwitch('settings')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeApp === 'settings'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Settings size={16} className="inline mr-2" />
                    Settings
                  </button>
                </div>
              </div>

              {/* Right side - User and tenant status with Sign Out Button */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {userProfile?.email || 'User'}
                  </div>
                  <div className="text-xs text-gray-600 capitalize">
                    {userProfile?.role?.replace('_', ' ') || 'User'}
                  </div>
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${
                    tenant.status === 'active' ? 'bg-green-500' :
                    tenant.status === 'inactive' ? 'bg-gray-400' : 'bg-red-500'
                  }`}></div>
                  {tenant.status}
                </div>
                <button
                  onClick={() => FirebaseService.signOut()}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Mobile Navigation Menu - Platform Owners Only */}
      {userProfile?.role === 'platform_owner' && (
        <div className="md:hidden bg-white border-b border-gray-200">
          <div className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
            <div className="flex gap-1 py-2 overflow-x-auto">
            {/* Dashboard - Platform Owners Only */}
            {userProfile?.role === 'platform_owner' && (
              <button
                onClick={() => handleAppSwitch('dashboard')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeApp === 'dashboard'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <BarChart3 size={16} className="inline mr-2" />
                Dashboard
              </button>
            )}

            {/* Routing Product - shown to all users */}
            <button
              onClick={() => handleAppSwitch('routing')}
              className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeApp === 'routing'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <Truck size={16} className="inline mr-2" />
              Routing
            </button>

            {tenant.features?.chainto && (
              <button
                onClick={() => handleAppSwitch('chainto')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeApp === 'chainto'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Package size={16} className="inline mr-2" />
                ChainTo
              </button>
            )}

            {/* Documents - Platform Owners Only */}
            {userProfile?.role === 'platform_owner' && (
              <button
                onClick={() => handleAppSwitch('documents')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeApp === 'documents'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FileText size={16} className="inline mr-2" />
                Documents
              </button>
            )}

            {/* Team Management Tab - Platform Owners Only */}
            {userProfile?.role === 'platform_owner' && (
              <button
                onClick={() => handleAppSwitch('team')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeApp === 'team'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Users size={16} className="inline mr-2" />
                Team
              </button>
            )}

            {/* Settings - Platform Owners Only */}
            {userProfile?.role === 'platform_owner' && (
              <button
                onClick={() => handleAppSwitch('settings')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeApp === 'settings'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Settings size={16} className="inline mr-2" />
                Settings
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* App Content */}
      <main className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
        {renderActiveApp()}
      </main>
    </div>
  </ErrorBoundary>
);

// Update your renderActiveApp function to include the team management case:
function renderActiveApp() {
  switch (activeApp) {
    case 'team':
      if (userProfile?.role === 'admin' || userProfile?.role === 'platform_owner') {
        return (
          <TenantUserManagement
            companyId={companyId}
            userProfile={userProfile}
            companyData={tenant}
          />
        );
      }
      return renderFeatureNotAvailable('Team Management');

    case 'routing':
      // Extract simple tenantId from companyId (e.g., 'demo' from 'company_demo')
      const tenantId = companyId.replace('company_', '');
      return (
        <RoutingDashboard
          tenantId={tenantId}
          userProfile={userProfile}
          onSignOut={() => FirebaseService.signOut()}
        />
      );

    case 'chainto':
      if (tenant.features?.chainto) {
        return renderChainToApp();
      }
      return renderFeatureNotAvailable('ChainTo Business Intelligence');

    case 'dashboard':
      return renderGeneralDashboard();

    case 'documents':
      return renderDocumentsApp();

    case 'settings':
      return renderSettingsApp();

    default:
      return renderGeneralDashboard();
  }
}

  /**
   * General tenant dashboard (keep all your existing functions)
   */
  function renderGeneralDashboard() {
    return (
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to {tenant.name}
          </h3>
          <p className="text-gray-600 mb-6">
            Your business intelligence platform is ready. Explore your data, generate insights, and make informed decisions.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{safeNumber(tenant.userCount)}</div>
              <div className="text-sm text-blue-800">Active Users</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {tenant.dataUsageMB ? 
                  `${safeNumber(Math.round(safeDivision(tenant.dataUsageMB, 1024) * 10) / 10)} GB` : 
                  '0 MB'
                }
              </div>
              <div className="text-sm text-green-800">Data Usage</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {tenant.features ? Object.values(tenant.features).filter(Boolean).length : 0}
              </div>
              <div className="text-sm text-purple-800">Active Features</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {safeDateDiff(tenant.createdAt, new Date())}
              </div>
              <div className="text-sm text-orange-800">Days Active</div>
            </div>
          </div>
        </div>

        {/* Features Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Available Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tenant.features?.chainto && (
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <Package size={20} className="text-green-600" />
                <div>
                  <div className="font-medium text-gray-900">ChainTo Business Intelligence</div>
                  <div className="text-sm text-gray-600">Supply chain analytics and cost management</div>
                </div>
              </div>
            )}
            
            {tenant.features?.analytics && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <BarChart3 size={20} className="text-blue-600" />
                <div>
                  <div className="font-medium text-gray-900">Advanced Analytics</div>
                  <div className="text-sm text-gray-600">Data visualization and business insights</div>
                </div>
              </div>
            )}
            
            {tenant.features?.reporting && (
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                <FileText size={20} className="text-purple-600" />
                <div>
                  <div className="font-medium text-gray-900">Reporting Suite</div>
                  <div className="text-sm text-gray-600">Automated reports and exports</div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Database size={20} className="text-gray-600" />
              <div>
                <div className="font-medium text-gray-900">Data Management</div>
                <div className="text-sm text-gray-600">Secure storage and processing</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleAppSwitch('documents')}
              className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Upload size={20} className="text-gray-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">Upload Documents</div>
                <div className="text-sm text-gray-600">Process new data files</div>
              </div>
            </button>

            {tenant.features?.chainto && (
              <button
                onClick={() => handleAppSwitch('chainto')}
                className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
              >
                <Package size={20} className="text-gray-600" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">ChainTo Analytics</div>
                  <div className="text-sm text-gray-600">Supply chain insights</div>
                </div>
              </button>
            )}

            <button
              onClick={() => handleAppSwitch('settings')}
              className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
            >
              <Settings size={20} className="text-gray-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">Settings</div>
                <div className="text-sm text-gray-600">Configure your account</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * ChainTo application placeholder
   */
  function renderChainToApp() {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Package size={20} />
          ChainTo Business Intelligence
        </h3>
        <div className="text-center py-12">
          <Package className="mx-auto text-gray-400 mb-4" size={48} />
          <h4 className="text-lg font-medium text-gray-900 mb-2">ChainTo Module Ready</h4>
          <p className="text-gray-600 mb-6">
            The ChainTo business intelligence module will be loaded here.
          </p>
          
          <div className="bg-blue-50 p-6 rounded-lg max-w-2xl mx-auto">
            <h5 className="font-medium text-blue-900 mb-3">ChainTo Features Include:</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-800">
              <div>• Supply chain analytics</div>
              <div>• Cost calculation</div>
              <div>• Duty allocation</div>
              <div>• Freight management</div>
              <div>• HTS code management</div>
              <div>• Business reporting</div>
            </div>
          </div>
          
          <div className="mt-6">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Launch ChainTo Module
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Documents management application
   */
  function renderDocumentsApp() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={20} />
            Document Management
          </h3>
          
          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
            <Upload className="mx-auto text-gray-400 mb-4" size={48} />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Upload Business Documents</h4>
            <p className="text-gray-600 mb-4">
              Drag and drop your Excel or CSV files here, or click to browse.
            </p>
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Choose Files
            </button>
          </div>

          {/* Supported Formats */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h5 className="font-medium text-gray-900 mb-2">Supported File Types:</h5>
            <div className="text-sm text-gray-600 grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>• Excel files (.xlsx, .xls)</div>
              <div>• CSV files (.csv)</div>
              <div>• Text files (.txt)</div>
            </div>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Recent Documents</h4>
          <div className="text-center py-8 text-gray-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p>No documents uploaded yet</p>
            <p className="text-sm">Upload your first document to get started</p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Settings application (keeping your existing settings function)
   */
  function renderSettingsApp() {
    return (
      <div className="space-y-6">
        {/* General Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings size={20} />
            Tenant Settings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">General Information</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <p className="text-sm text-gray-900">{tenant.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Industry</label>
                  <p className="text-sm text-gray-900">{tenant.industry || 'Not specified'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                    tenant.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Admin Email</label>
                  <p className="text-sm text-gray-900">{tenant.adminEmail || 'Not specified'}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Infrastructure</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">BigQuery Dataset</label>
                  <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                    {tenant.bigqueryDataset}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Storage Bucket</label>
                  <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                    {tenant.storageBucket}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-900">
                    {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Platform Version</label>
                  <p className="text-sm text-gray-900">{tenant.version || '2.1.0'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Enabled Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(tenant.features || {}).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium text-gray-900 capitalize">{feature}</span>
                  {feature === 'chainto' && (
                    <p className="text-xs text-gray-600">Supply chain business intelligence</p>
                  )}
                  {feature === 'analytics' && (
                    <p className="text-xs text-gray-600">Advanced data analytics</p>
                  )}
                  {feature === 'reporting' && (
                    <p className="text-xs text-gray-600">Automated reporting suite</p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Usage Statistics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{safeNumber(tenant.userCount)}</div>
              <div className="text-sm text-blue-800">Total Users</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {tenant.dataUsageMB ? 
                  `${safeDivision(tenant.dataUsageMB, 1024).toFixed(1)} GB` : 
                  '0 MB'
                }
              </div>
              <div className="text-sm text-green-800">Data Storage</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {safeDateDiff(tenant.lastActivity, new Date())}
              </div>
              <div className="text-sm text-purple-800">Days Since Activity</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Feature not available message
   */
  function renderFeatureNotAvailable(featureName) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
        <AlertCircle className="mx-auto text-orange-500 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Not Available</h3>
        <p className="text-gray-600 mb-4">
          {featureName} is not enabled for this tenant.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Contact your administrator to enable this feature.
        </p>
        <button
          onClick={() => handleAppSwitch('settings')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          View Settings
        </button>
      </div>
    );
  }
}
